import React, { useEffect } from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import { Box, Button, Divider } from '@mui/material';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import CloseIcon from '@mui/icons-material/Close';
import InputAdornment from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import RestrictedLocationsList from './RestrictedLocationsList/RestrictedLocationsList';
import { isOptionEqualToValue } from '../utils';
import { useGetInventoryOwnersQuery, useGetLocationsQuery } from '../batchesEndpoints';

const contentWrapper = {
  display: 'flex',
  justifyContent: 'space-between',
  mb: 1.5,
};

const EditViewDistribution = ({
  availableVolumeExceeded,
  distribution,
  index,
  isFetchingRecipients,
  isRecipientsSuccess,
  handleChainRecipientsChange,
  handleInventoryOwnerChange,
  handleLocationChange,
  handleReconsignmentControlChange,
  handleVolumeChange,
  handleShipmentChainChange,
  mode,
  recipientsQuery,
  renderTags,
  renderDeleteDialog,
  setValidationMessages,
  shipmentData,
  isNew,
  validationMessages,
}) => {
  const isEditMode = mode;
  const dataCompanyId = R.path(['shipper', 'dataCompanyId'], shipmentData);

  const inventoryOwnersParams = { dataCompanyId };

  const {
    data: inventoryOwnersData = [],
    isFetching: isFetchingInventoryOwners,
    isSuccess: isSuccessInventoryOwners,
  } = useGetInventoryOwnersQuery(
    { params: inventoryOwnersParams },
    { skip: R.isNil(dataCompanyId) }
  );

  const extractFirstIntegersFromString = str => {
    if (!str) return null;
    const matches = str.match(/(\d+)/g);
    if (matches && matches.length > 0) {
      return parseInt(matches[0], 10);
    }
    return null;
  };

  const locationsParams = {
    dataCompanyId: R.prop('shipper', shipmentData)?.dataCompanyId,
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    bookingOriginId: R.prop('bookingOriginId', shipmentData),
    currentLocationId: R.prop('locationId', distribution),
    productId: R.prop('productId', shipmentData),

    shipmentId: R.prop('shipmentId', shipmentData),
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    inventoryOwner: {
      companyCustomerIdName: extractFirstIntegersFromString(
        R.prop('inventoryOwnerName', distribution)
      ),
      dataCompanyId: R.prop('shipper', shipmentData)?.dataCompanyId,
    },
  };

  const {
    data: locationsData = [],
    isFetching: isFetchingLocations,
    isSuccess: isSuccessLocations,
    refetch: refetchLocations,
  } = useGetLocationsQuery(
    { params: locationsParams },
    {
      skip:
        R.isNil(locationsParams.inventoryOwner.companyCustomerIdName) ||
        R.isNil(dataCompanyId) ||
        R.isNil(locationsParams.bookingOriginId),
    }
  );

  const onInventoryOwnerChange = (distributionId, value) => {
    if (value && value.inventoryOwnerName) {
      handleLocationChange(distribution.distributionId, index, null);
      refetchLocations();
      handleInventoryOwnerChange(distributionId, index, value);
    }
  };

  useEffect(() => {
    if (!isSuccessLocations || R.isEmpty(locationsData)) {
      return;
    }

    const isLocationInList = locationsData.some(
      loc => loc.locationName === distribution.locationName
    );

    setValidationMessages(state => {
      const newState = R.clone(state);
      // eslint-disable-next-line no-param-reassign
      newState[index] = R.assoc(
        'locationName',
        isLocationInList
          ? ''
          : 'Only locations that can accept the product and have a valid tariff route from the origin can be selected',
        newState[index]
      );
      return newState;
    });
  }, [distribution, index, isSuccessLocations, locationsData, setValidationMessages]);

  return (
    <Box>
      <Stack spacing={2} sx={{ py: { md: 3, xs: 2 } }}>
        <Divider />
        <Box sx={contentWrapper}>
          <Box>
            <Typography variant="primary">{distribution.distributionId}</Typography>
            <Typography variant="subtitle2">
              Distribution {R.prop('distributionId', distribution) ? '' : index + 1}
            </Typography>
          </Box>
          {isEditMode && (
            <Button
              data-testid="delete-distribution-button"
              onClick={() => renderDeleteDialog(R.prop('distributionId', distribution), index)}
              startIcon={<CloseIcon sx={{ color: 'error.main', fontSize: 32, mt: '-2px' }} />}
              sx={{
                '&:hover': {
                  backgroundColor: 'error.light',
                  color: 'error.dark',
                },
                color: 'error.main',
              }}
              variant="text"
            >
              Delete
            </Button>
          )}
        </Box>

        <Autocomplete
          disableClearable
          disabled={!isSuccessInventoryOwners || !isNew}
          getOptionLabel={R.propOr('', 'inventoryOwnerName')}
          isOptionEqualToValue={isOptionEqualToValue('inventoryOwnerName')}
          loading={isFetchingInventoryOwners}
          onChange={(_, value) => onInventoryOwnerChange(distribution.distributionId, value)}
          options={inventoryOwnersData}
          renderInput={inputParams => <TextField {...inputParams} label="Inventory Owner" />}
          value={
            inventoryOwnersData.find(
              owner => owner.inventoryOwnerName === distribution.inventoryOwnerName
            ) || null
          }
        />

        <Stack spacing={1}>
          <Autocomplete
            disableClearable
            disabled={R.or(!isSuccessLocations, R.isEmpty(locationsData)) || !isEditMode}
            getOptionLabel={R.propOr('', 'locationName')}
            isOptionEqualToValue={isOptionEqualToValue('locationName')}
            loading={isFetchingLocations}
            onChange={(_, value) => {
              handleLocationChange(distribution.distributionId, index, value);
            }}
            options={locationsData}
            renderInput={props => (
              <TextField
                {...props}
                error={R.and(
                  !R.isEmpty(validationMessages[index]?.locationName),
                  !R.isNil(validationMessages[index]?.locationName)
                )}
                fullWidth
                helperText={validationMessages[index]?.locationName || ''}
                label="Location"
              />
            )}
            value={
              locationsData.find(loc => loc.locationName === distribution.locationName) || {
                id: 0,
                locationName: distribution.locationName,
              }
            }  // eslint-disable-line
          />
          {R.and(isSuccessLocations, R.isEmpty(locationsData)) && (
            <Alert severity="warning">
              There are no valid locations available for selection. Please contact the Batch Owner
              to update the origin or product on the parent batch.
            </Alert>
          )}
        </Stack>

        <TextField
          disabled={!isEditMode}
          error={R.or(
            R.and(
              !R.isEmpty(validationMessages[index]?.volume),
              !R.isNil(validationMessages[index]?.volume)
            ),
            !!availableVolumeExceeded
          )}
          fullWidth
          helperText={
            availableVolumeExceeded
              ? 'Distribution Volume causes the total distributed volume to exceed the Shipment Volume'
              : validationMessages[index]?.volume || ''
          }
          label="Volume"
          onChange={event => {
            return handleVolumeChange(distribution.distributionId, index, event.target?.value);
          }}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">bbl</InputAdornment>,
            },
          }}
          value={formatNumberWithoutDecimals(R.prop('volume', distribution) ?? '')}
        />

        <FormControlLabel
          control={
            <Switch
              checked={!!distribution?.shipmentChain}
              color="secondary"
              disabled={!isEditMode}
              onChange={evt => handleShipmentChainChange(index, evt)}
            />
          }
          label={<Typography>Is a shipment chain required?</Typography>}
        />

        {!!distribution?.shipmentChain && Array.isArray(distribution?.chainRecipients) && (
          <Autocomplete
            disableClearable
            disabled={!isRecipientsSuccess || !isEditMode}
            getOptionLabel={R.propOr('', 'recipientName')}
            isOptionEqualToValue={isOptionEqualToValue('recipientIdName')}
            loading={isFetchingRecipients}
            multiple
            onChange={(_, value) => handleChainRecipientsChange(index, value)}
            options={recipientsQuery}
            renderInput={inputParams => (
              <TextField
                {...inputParams}
                error={R.and(
                  !R.isEmpty(validationMessages[index]?.chainRecipients),
                  !R.isNil(validationMessages[index]?.chainRecipients)
                )}
                helperText={validationMessages[index]?.chainRecipients || ''}
                label="Recipients"
              />
            )}
            renderTags={renderTags}
            value={R.prop('chainRecipients', distribution)}
          />
        )}

        <FormControlLabel
          control={
            <Switch
              checked={!!distribution.reconsignmentControl}
              color="secondary"
              disabled={
                R.or(
                  R.isNil(distribution.inventoryOwnerName),
                  R.isNil(distribution.locationName)
                ) || !isEditMode
              }
              onChange={evt => handleReconsignmentControlChange(index, evt)}
            />
          }
          label={
            <Stack alignItems="center" direction="row" spacing={2}>
              <Typography>Set a reconsignment control</Typography>
              <Tooltip title="A Reconsignment Control maximizes efficiencies by limiting loading of transferred barrels to the selected terminal location or a location further from the origin.">
                <InfoRoundedIcon sx={{ color: 'graphic.main' }} />
              </Tooltip>
            </Stack>
          }
        />

        {distribution.reconsignmentControl && !R.isNil(locationsData) && (
          <RestrictedLocationsList
            inventoryOwner={R.defaultTo(
              {},
              R.find(
                R.propEq('inventoryOwnerName', distribution.inventoryOwnerName),
                inventoryOwnersData
              )
            )}
            locationId={R.defaultTo(
              null,
              R.find(R.propEq('locationName', distribution.locationName), locationsData)
            )}
            originId={R.prop('originId', shipmentData)}
            productId={R.prop('productId', shipmentData)}
          />
        )}
      </Stack>
    </Box>
  );
};

EditViewDistribution.propTypes = {
  availableVolumeExceeded: PropTypes.bool.isRequired,
  distribution: PropTypes.shape({
    chainRecipients: PropTypes.arrayOf(
      PropTypes.shape({
        recipientIdName: PropTypes.string.isRequired,
        recipientName: PropTypes.string.isRequired,
      })
    ),
    distributionId: PropTypes.number,
    inventoryOwnerName: PropTypes.string,
    locationName: PropTypes.string,
    reconsignmentControl: PropTypes.bool,
    shipmentChain: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    volume: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  handleChainRecipientsChange: PropTypes.func.isRequired,
  handleInventoryOwnerChange: PropTypes.func.isRequired,
  handleLocationChange: PropTypes.func.isRequired,
  handleReconsignmentControlChange: PropTypes.func.isRequired,
  handleShipmentChainChange: PropTypes.func.isRequired,
  handleVolumeChange: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  isFetchingRecipients: PropTypes.bool.isRequired,
  isNew: PropTypes.bool,
  isRecipientsSuccess: PropTypes.bool.isRequired,
  mode: PropTypes.bool,
  recipientsQuery: PropTypes.arrayOf(
    PropTypes.shape({
      recipientIdName: PropTypes.string.isRequired,
      recipientName: PropTypes.string.isRequired,
    })
  ).isRequired,
  renderDeleteDialog: PropTypes.func,
  renderTags: PropTypes.func.isRequired,
  setValidationMessages: PropTypes.func.isRequired,
  shipmentData: PropTypes.shape({
    originId: PropTypes.number,
    productId: PropTypes.number,
    shipper: PropTypes.shape({
      dataCompanyId: PropTypes.number,
    }),
  }).isRequired,
  validationMessages: PropTypes.arrayOf(
    PropTypes.shape({
      chainRecipients: PropTypes.string,
      locationName: PropTypes.string,
      volume: PropTypes.string,
    })
  ).isRequired,
};

EditViewDistribution.defaultProps = {
  isNew: false,
  mode: 'view',
  renderDeleteDialog: () => {},
};

export default EditViewDistribution;
